import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const UserPreferencesTab = () => {
  const [preferences, setPreferences] = useState({
    defaultCurrency: 'GBP',
    dateFormat: 'DD/MM/YYYY',
    timeFormat: '24h',
    timezone: 'Europe/London',
    language: 'en-GB',
    timeSavedBaseline: 300,
    autoSaveInterval: 30,
    notificationSettings: {
      emailNotifications: true,
      browserNotifications: true,
      processingComplete: true,
      errorAlerts: true,
      weeklyReports: false,
      systemUpdates: true
    },
    displaySettings: {
      compactView: false,
      showConfidenceScores: true,
      highlightLowConfidence: true,
      showProcessingTime: true,
      defaultPageSize: 25
    },
    exportDefaults: {
      includeHeaders: true,
      dateFormat: 'DD/MM/YYYY',
      numberFormat: 'UK',
      defaultTransactionType: 'SI'
    }
  });

  const currencyOptions = [
    { value: 'GBP', label: '£ British Pound (GBP)' },
    { value: 'EUR', label: '€ Euro (EUR)' },
    { value: 'USD', label: '$ US Dollar (USD)' }
  ];

  const dateFormatOptions = [
    { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY (UK Format)' },
    { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY (US Format)' },
    { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD (ISO Format)' }
  ];

  const timeFormatOptions = [
    { value: '24h', label: '24-hour (14:30)' },
    { value: '12h', label: '12-hour (2:30 PM)' }
  ];

  const timezoneOptions = [
    { value: 'Europe/London', label: 'London (GMT/BST)' },
    { value: 'Europe/Dublin', label: 'Dublin (GMT/IST)' },
    { value: 'Europe/Paris', label: 'Paris (CET/CEST)' },
    { value: 'America/New_York', label: 'New York (EST/EDT)' }
  ];

  const languageOptions = [
    { value: 'en-GB', label: 'English (UK)' },
    { value: 'en-US', label: 'English (US)' },
    { value: 'fr-FR', label: 'Français (France)' },
    { value: 'de-DE', label: 'Deutsch (Deutschland)' }
  ];

  const pageSizeOptions = [
    { value: 10, label: '10 items per page' },
    { value: 25, label: '25 items per page' },
    { value: 50, label: '50 items per page' },
    { value: 100, label: '100 items per page' }
  ];

  const transactionTypeOptions = [
    { value: 'SI', label: 'Sales Invoice (SI)' },
    { value: 'PI', label: 'Purchase Invoice (PI)' },
    { value: 'SC', label: 'Sales Credit (SC)' },
    { value: 'PC', label: 'Purchase Credit (PC)' }
  ];

  const handlePreferenceChange = (field, value) => {
    setPreferences(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleNestedPreferenceChange = (section, field, value) => {
    setPreferences(prev => ({
      ...prev,
      [section]: {
        ...prev?.[section],
        [field]: value
      }
    }));
  };

  const handleSavePreferences = () => {
    console.log('Saving preferences:', preferences);
  };

  const handleResetPreferences = () => {
    setPreferences({
      defaultCurrency: 'GBP',
      dateFormat: 'DD/MM/YYYY',
      timeFormat: '24h',
      timezone: 'Europe/London',
      language: 'en-GB',
      timeSavedBaseline: 300,
      autoSaveInterval: 30,
      notificationSettings: {
        emailNotifications: true,
        browserNotifications: true,
        processingComplete: true,
        errorAlerts: true,
        weeklyReports: false,
        systemUpdates: true
      },
      displaySettings: {
        compactView: false,
        showConfidenceScores: true,
        highlightLowConfidence: true,
        showProcessingTime: true,
        defaultPageSize: 25
      },
      exportDefaults: {
        includeHeaders: true,
        dateFormat: 'DD/MM/YYYY',
        numberFormat: 'UK',
        defaultTransactionType: 'SI'
      }
    });
  };

  return (
    <div className="space-y-8">
      {/* General Preferences */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
            <Icon name="Settings" size={20} className="text-primary" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">General Preferences</h3>
            <p className="text-sm text-muted-foreground">Configure your default settings and formats</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-4">
            <Select
              label="Default Currency"
              options={currencyOptions}
              value={preferences?.defaultCurrency}
              onChange={(value) => handlePreferenceChange('defaultCurrency', value)}
              description="Primary currency for new invoices"
            />

            <Select
              label="Date Format"
              options={dateFormatOptions}
              value={preferences?.dateFormat}
              onChange={(value) => handlePreferenceChange('dateFormat', value)}
              description="How dates are displayed throughout the app"
            />

            <Select
              label="Time Format"
              options={timeFormatOptions}
              value={preferences?.timeFormat}
              onChange={(value) => handlePreferenceChange('timeFormat', value)}
            />

            <Select
              label="Timezone"
              options={timezoneOptions}
              value={preferences?.timezone}
              onChange={(value) => handlePreferenceChange('timezone', value)}
              searchable
            />
          </div>

          <div className="space-y-4">
            <Select
              label="Language"
              options={languageOptions}
              value={preferences?.language}
              onChange={(value) => handlePreferenceChange('language', value)}
              description="Interface language"
            />

            <Input
              type="number"
              label="Time Saved Baseline (seconds)"
              value={preferences?.timeSavedBaseline}
              onChange={(e) => handlePreferenceChange('timeSavedBaseline', parseInt(e?.target?.value))}
              description="Average time to process one invoice manually"
              min={60}
              max={1800}
            />

            <Input
              type="number"
              label="Auto-save Interval (seconds)"
              value={preferences?.autoSaveInterval}
              onChange={(e) => handlePreferenceChange('autoSaveInterval', parseInt(e?.target?.value))}
              description="How often to automatically save changes"
              min={10}
              max={300}
            />
          </div>
        </div>
      </div>
      {/* Notification Settings */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
            <Icon name="Bell" size={20} className="text-accent" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">Notification Settings</h3>
            <p className="text-sm text-muted-foreground">Choose how you want to be notified</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <h4 className="font-medium text-foreground">Delivery Methods</h4>
            
            <Checkbox
              label="Email Notifications"
              description="Receive notifications via email"
              checked={preferences?.notificationSettings?.emailNotifications}
              onChange={(e) => handleNestedPreferenceChange('notificationSettings', 'emailNotifications', e?.target?.checked)}
            />

            <Checkbox
              label="Browser Notifications"
              description="Show desktop notifications in browser"
              checked={preferences?.notificationSettings?.browserNotifications}
              onChange={(e) => handleNestedPreferenceChange('notificationSettings', 'browserNotifications', e?.target?.checked)}
            />
          </div>

          <div className="space-y-4">
            <h4 className="font-medium text-foreground">Notification Types</h4>
            
            <Checkbox
              label="Processing Complete"
              description="When batch processing finishes"
              checked={preferences?.notificationSettings?.processingComplete}
              onChange={(e) => handleNestedPreferenceChange('notificationSettings', 'processingComplete', e?.target?.checked)}
            />

            <Checkbox
              label="Error Alerts"
              description="When processing errors occur"
              checked={preferences?.notificationSettings?.errorAlerts}
              onChange={(e) => handleNestedPreferenceChange('notificationSettings', 'errorAlerts', e?.target?.checked)}
            />

            <Checkbox
              label="Weekly Reports"
              description="Summary of weekly activity"
              checked={preferences?.notificationSettings?.weeklyReports}
              onChange={(e) => handleNestedPreferenceChange('notificationSettings', 'weeklyReports', e?.target?.checked)}
            />

            <Checkbox
              label="System Updates"
              description="New features and maintenance notices"
              checked={preferences?.notificationSettings?.systemUpdates}
              onChange={(e) => handleNestedPreferenceChange('notificationSettings', 'systemUpdates', e?.target?.checked)}
            />
          </div>
        </div>
      </div>
      {/* Display Settings */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-10 h-10 bg-secondary/10 rounded-lg flex items-center justify-center">
            <Icon name="Monitor" size={20} className="text-secondary" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">Display Settings</h3>
            <p className="text-sm text-muted-foreground">Customize how information is displayed</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <Checkbox
              label="Compact View"
              description="Show more items in less space"
              checked={preferences?.displaySettings?.compactView}
              onChange={(e) => handleNestedPreferenceChange('displaySettings', 'compactView', e?.target?.checked)}
            />

            <Checkbox
              label="Show Confidence Scores"
              description="Display AI confidence percentages"
              checked={preferences?.displaySettings?.showConfidenceScores}
              onChange={(e) => handleNestedPreferenceChange('displaySettings', 'showConfidenceScores', e?.target?.checked)}
            />

            <Checkbox
              label="Highlight Low Confidence"
              description="Emphasize fields with low confidence"
              checked={preferences?.displaySettings?.highlightLowConfidence}
              onChange={(e) => handleNestedPreferenceChange('displaySettings', 'highlightLowConfidence', e?.target?.checked)}
            />
          </div>

          <div className="space-y-4">
            <Checkbox
              label="Show Processing Time"
              description="Display how long processing took"
              checked={preferences?.displaySettings?.showProcessingTime}
              onChange={(e) => handleNestedPreferenceChange('displaySettings', 'showProcessingTime', e?.target?.checked)}
            />

            <Select
              label="Default Page Size"
              options={pageSizeOptions}
              value={preferences?.displaySettings?.defaultPageSize}
              onChange={(value) => handleNestedPreferenceChange('displaySettings', 'defaultPageSize', value)}
              description="Number of items to show per page"
            />
          </div>
        </div>
      </div>
      {/* Export Defaults */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-10 h-10 bg-success/10 rounded-lg flex items-center justify-center">
            <Icon name="Download" size={20} className="text-success" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">Export Defaults</h3>
            <p className="text-sm text-muted-foreground">Default settings for Sage 50 exports</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-4">
            <Checkbox
              label="Include Headers"
              description="Add column headers to CSV exports"
              checked={preferences?.exportDefaults?.includeHeaders}
              onChange={(e) => handleNestedPreferenceChange('exportDefaults', 'includeHeaders', e?.target?.checked)}
            />

            <Select
              label="Export Date Format"
              options={dateFormatOptions}
              value={preferences?.exportDefaults?.dateFormat}
              onChange={(value) => handleNestedPreferenceChange('exportDefaults', 'dateFormat', value)}
              description="Date format in exported files"
            />
          </div>

          <div className="space-y-4">
            <Select
              label="Default Transaction Type"
              options={transactionTypeOptions}
              value={preferences?.exportDefaults?.defaultTransactionType}
              onChange={(value) => handleNestedPreferenceChange('exportDefaults', 'defaultTransactionType', value)}
              description="Default transaction type for Sage export"
            />

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Number Format
              </label>
              <div className="text-sm text-muted-foreground">
                UK format (1,234.56) - automatically applied based on locale
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Action Buttons */}
      <div className="flex items-center justify-between pt-6 border-t border-border">
        <Button 
          variant="outline" 
          onClick={handleResetPreferences}
          iconName="RotateCcw"
          iconPosition="left"
        >
          Reset to Defaults
        </Button>
        
        <Button 
          onClick={handleSavePreferences}
          iconName="Save"
          iconPosition="left"
        >
          Save Preferences
        </Button>
      </div>
    </div>
  );
};

export default UserPreferencesTab;